import { RegistroDto, UpdateRegistroDto } from "@/api";
import Box from "@/elements/Box";
import NumberInput from "@/elements/Input/NumberInput";
import {
  Card,
  FormHelperText,
  Grid,
  InputAdornment,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import TextField from "@mui/material/TextField";
import { isNil, keyBy } from "lodash";
import { Control, Controller } from "react-hook-form";

type NumberFieldConfig = {
  name: string;
  label: string;
  plcType: "real" | "int";
  decimals?: number;
  min?: number;
  max?: number;
  unit?: string;
};

type BoolFieldConfig = {
  name: string;
  label: string;
};

type TextFieldConfig = {
  name: string;
  label: string;
};

const numericFields: NumberFieldConfig[] = [
  {
    name: "mintospo",
    plcType: "real",
    label: "Temperatura minima olio sporco",
    decimals: 1,
    unit: "°C",
  },
  {
    name: "mintopul",
    plcType: "real",
    label: "Temperatura minima olio pulito",
    decimals: 1,
    unit: "°C",
  },
  {
    name: "maxpreshc",
    plcType: "real",
    label: "Pressione massima HC400",
    decimals: 1,
    unit: "bar",
  },
  {
    name: "maxprestfl",
    plcType: "real",
    label: "Pressione massima TFL700",
    decimals: 1,
    unit: "bar",
  },
  {
    name: "minpreshc",
    plcType: "real",
    label: "Pressione minima HC400",
    decimals: 1,
    unit: "bar",
  },
  {
    name: "tpresminhc",
    plcType: "int",
    label: "Ritardo allarme pressione minima HC400",
    min: 0,
  },
  {
    name: "minprestfl",
    plcType: "real",
    label: "Pressione minima TFL700",
    decimals: 1,
    unit: "bar",
  },
  {
    name: "tpresmintfl",
    plcType: "int",
    label: "Ritardo allarme pressione minima TFL700",
    min: 0,
  },
  {
    name: "maxsatopul",
    plcType: "real",
    label: "Saturazione massima olio pulito",
    min: 0,
    unit: "%",
  },
];

const boolFields: BoolFieldConfig[] = [
  { name: "funzman", label: "Selettore manuale/automatico" },
  { name: "abiltfl", label: "TFL700 incluso" },
  { name: "abilhc", label: "HC400 incluso" },
];

const textFields: TextFieldConfig[] = [
  { name: "email1", label: "Email 1" },
  { name: "email2", label: "Email 2" },
];

const supportedFields = [
  ...numericFields.map((x) => x.name),
  ...boolFields.map((x) => x.name),
  ...textFields.map((x) => x.name),
] as const;

type RegistroFieldName = (typeof supportedFields)[number];
type Registri_GFL_TH_4000 = Record<RegistroFieldName, any>;

const numericFieldMap = Object.fromEntries(
  numericFields.map((field) => [field.name, field])
) as Record<string, NumberFieldConfig>;

const boolFieldMap = Object.fromEntries(
  boolFields.map((field) => [field.name, field])
) as Record<string, BoolFieldConfig>;

const textFieldMap = Object.fromEntries(
  textFields.map((field) => [field.name, field])
) as Record<string, TextFieldConfig>;

function parseNumber(value: string | undefined) {
  if (isNil(value) || value === "") {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function parseBool(value: string | undefined) {
  if (isNil(value)) {
    return false;
  }
  const normalized = String(value).trim().toLowerCase();
  return ["1", "true", "on"].includes(normalized);
}

function mapEmptyStringToSlash(value: string) {
  return value === "" ? "/" : value;
}

// i PLC Siemens vogliono i real in notazione scientifica (es. 0.4 => "4.000000E-1"), come GFLTFL700
function formatNumber(value: number, plcType: NumberFieldConfig["plcType"]) {
  if (plcType === "int") {
    return Math.floor(value).toString();
  }
  return value.toExponential(6).replace("e", "E");
}

export function registriToForm_GFL_TH_4000(
  _registri: RegistroDto[]
): Partial<Registri_GFL_TH_4000> {
  const registri = keyBy(_registri, "registro");
  const result: Partial<Registri_GFL_TH_4000> = {};

  for (const field of supportedFields) {
    const raw = registri[field]?.value;

    if (numericFieldMap[field]) {
      result[field] = parseNumber(raw);
      continue;
    }

    if (boolFieldMap[field]) {
      result[field] = parseBool(raw);
      continue;
    }

    result[field] = raw === "/" ? "" : raw || "";
  }

  return result;
}

export function formToRegistri_GFL_TH_4000(
  form: Partial<Registri_GFL_TH_4000>
): UpdateRegistroDto[] {
  const topicPrefix = "var";

  return supportedFields
    .map((field) => {
      const value = form[field];
      if (isNil(value)) {
        return undefined;
      }

      const numericField = numericFieldMap[field];
      if (numericField) {
        return {
          registro: field,
          topicPrefix,
          value: formatNumber(Number(value), numericField.plcType),
        };
      }

      if (boolFieldMap[field]) {
        return { registro: field, topicPrefix, value: value ? "1" : "0" };
      }

      return {
        registro: field,
        topicPrefix,
        value: mapEmptyStringToSlash(String(value)),
      };
    })
    .filter(Boolean) as UpdateRegistroDto[];
}

export function EditorRegistri_GFL_TH_4000({
  control,
  readOnly = false,
}: {
  control: Control<Registri_GFL_TH_4000>;
  readOnly?: boolean;
}) {
  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <SectionCard title="Soglie temperatura">
            <Grid item xs={12}>
              <NumberRegistroCell
                control={control}
                fieldName="mintospo"
                readOnly={readOnly}
              />
            </Grid>
            <Grid item xs={12}>
              <NumberRegistroCell
                control={control}
                fieldName="mintopul"
                readOnly={readOnly}
              />
            </Grid>
            <Grid item xs={12}>
              <NumberRegistroCell
                control={control}
                fieldName="maxsatopul"
                readOnly={readOnly}
              />
            </Grid>
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionCard title="Soglie pressione">
            <Grid item xs={12}>
              <NumberRegistroCell
                control={control}
                fieldName="maxpreshc"
                readOnly={readOnly}
              />
            </Grid>
            <Grid item xs={12}>
              <NumberRegistroCell
                control={control}
                fieldName="maxprestfl"
                readOnly={readOnly}
              />
            </Grid>
            <Grid item xs={12}>
              <NumberRegistroCell
                control={control}
                fieldName="minpreshc"
                readOnly={readOnly}
              />
            </Grid>
            <Grid item xs={12}>
              <NumberRegistroCell
                control={control}
                fieldName="minprestfl"
                readOnly={readOnly}
              />
            </Grid>
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionCard title="Tempi allarmi">
            <Grid item xs={12}>
              <NumberRegistroCell
                control={control}
                fieldName="tpresminhc"
                readOnly={readOnly}
              />
            </Grid>
            <Grid item xs={12}>
              <NumberRegistroCell
                control={control}
                fieldName="tpresmintfl"
                readOnly={readOnly}
              />
            </Grid>
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionCard title="Configurazione macchina">
            <Grid item xs={12}>
              <BooleanField
                control={control}
                config={boolFieldMap.funzman}
                readOnly={readOnly}
              />
            </Grid>
            <Grid item xs={12}>
              <BooleanField
                control={control}
                config={boolFieldMap.abiltfl}
                readOnly={readOnly}
              />
            </Grid>
            <Grid item xs={12}>
              <BooleanField
                control={control}
                config={boolFieldMap.abilhc}
                readOnly={readOnly}
              />
            </Grid>
          </SectionCard>
        </Grid>

        <Grid item xs={12}>
          <SectionCard title="Email">
            <Grid item xs={12} md={6}>
              <TextRegistroCell
                control={control}
                fieldName="email1"
                readOnly={readOnly}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextRegistroCell
                control={control}
                fieldName="email2"
                readOnly={readOnly}
              />
            </Grid>
          </SectionCard>
        </Grid>
      </Grid>
    </Box>
  );
}

function NumberField({
  control,
  config,
  readOnly,
}: {
  control: Control<Registri_GFL_TH_4000>;
  config: NumberFieldConfig;
  readOnly: boolean;
}) {
  return (
    <Controller
      control={control}
      name={config.name as any}
      rules={{
        validate: (value) => {
          if (isNil(value) || value === "") {
            return true;
          }

          const numeric = Number(value);
          if (!Number.isFinite(numeric)) {
            return "Inserisci un numero valido";
          }

          if (!isNil(config.min) && numeric < config.min) {
            return `Il valore deve essere maggiore o uguale a ${config.min}`;
          }

          if (!isNil(config.max) && numeric > config.max) {
            return `Il valore deve essere minore o uguale a ${config.max}`;
          }

          return true;
        },
      }}
      render={({ field, fieldState: { error } }) => (
        <Stack
          direction="column"
          alignItems="end"
          sx={{ width: "100%", maxWidth: 200 }}
        >
          <NumberInput
            {...field}
            numericInputProps={{ decimalScale: config.decimals ?? 0 }}
            endAdornment={
              config.unit ? (
                <InputAdornment position="end">{config.unit}</InputAdornment>
              ) : undefined
            }
            disabled={readOnly}
          />
          {error && <FormHelperText error>{error.message}</FormHelperText>}
        </Stack>
      )}
    />
  );
}

function NumberRegistroCell({
  control,
  fieldName,
  readOnly,
}: {
  control: Control<Registri_GFL_TH_4000>;
  fieldName: RegistroFieldName;
  readOnly: boolean;
}) {
  const config = numericFieldMap[fieldName];
  if (!config) {
    return null;
  }

  return (
    <Box display="flex" flexDirection="row" gap={0.5}>
      <Typography variant="button" fontWeight="regular" color="text" flex={1}>
        {config.label}
      </Typography>
      <NumberField control={control} config={config} readOnly={readOnly} />
    </Box>
  );
}

function BooleanField({
  control,
  config,
  readOnly,
}: {
  control: Control<Registri_GFL_TH_4000>;
  config: BoolFieldConfig;
  readOnly: boolean;
}) {
  return (
    <Controller
      name={config.name as any}
      control={control}
      render={({ field }) => (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography
            variant="button"
            fontWeight="regular"
            color="text"
            sx={{ minWidth: 220 }}
          >
            {config.label}
          </Typography>
          <Switch
            checked={field.value || false}
            onChange={(event) => field.onChange(event.target.checked)}
            disabled={readOnly}
          />
        </Stack>
      )}
    />
  );
}

function TextRegistroCell({
  control,
  fieldName,
  readOnly,
}: {
  control: Control<Registri_GFL_TH_4000>;
  fieldName: RegistroFieldName;
  readOnly: boolean;
}) {
  const config = textFieldMap[fieldName];
  if (!config) {
    return null;
  }

  return (
    <Box display="flex" flexDirection="column" gap={0.5}>
      <Typography variant="button" fontWeight="regular" color="text">
        {config.label}
      </Typography>
      <Controller
        control={control}
        name={config.name as any}
        render={({ field, fieldState: { error } }) => (
          <Stack direction="column" alignItems="end" sx={{ width: "100%" }}>
            <TextField
              {...field}
              size="small"
              fullWidth
              disabled={readOnly}
              error={!!error}
            />
            {error && <FormHelperText error>{error.message}</FormHelperText>}
          </Stack>
        )}
      />
    </Box>
  );
}

function SectionCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <Box p={2}>
        <Typography variant="h5" color="text" fontWeight="medium" gutterBottom>
          {title}
        </Typography>
        <Grid container spacing={2}>
          {children}
        </Grid>
      </Box>
    </Card>
  );
}
